/**
 * Mobile Drawer Support for Dashboard Tooltips (touch devices only; auto-exits if no touchstart/maxTouchPoints).
 * Converts hover-tooltips to tap-to-open drawer on touch devices, preventing tap-to-scroll conflicts.
 * Creates persistent overlay + drawer with stopPropagation on touchmove (no body scroll during drawer interaction).
 * Registers tap handlers on all .dashboard-tooltip elements; tracks open/closing state to prevent click-through.
 */

(function () {
  'use strict';

  // Conditional logging setup — delegate to core's isDev flag when available (single source of truth).
  // Local fallback covers the unlikely case this module loads before core.js.
  const isDev =
    window.AdminDashboardCore?.isDev ??
    (window.location.hostname === 'localhost' || window.location.hostname.includes('.dev'));
  const log = isDev ? console.log.bind(console) : () => {};

  // Detect if device supports touch
  const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;

  function safeSetHTML(element, htmlString) {
    window.AdminDashboardUI?.safeSetHTML(element, htmlString);
  }

  // ---------------------------------------------------------------------------
  // Stats section collapse — starts collapsed so Real-Time Activity is visible
  // immediately on mobile without scrolling.
  // ---------------------------------------------------------------------------

  /** CSS class that hides the stats grid via display:none */
  const STATS_GRID_COLLAPSED_CLASS = 'stats-grid--collapsed';

  function initStatsSectionCollapse() {
    const toggle = document.getElementById('stats-section-toggle');
    const grid = document.getElementById('stats-grid');
    if (!toggle || !grid) return;

    // Grid starts collapsed (class already set in HTML); sync aria state.
    toggle.setAttribute('aria-expanded', 'false');

    toggle.addEventListener('click', () => {
      const isExpanded = toggle.getAttribute('aria-expanded') === 'true';

      if (isExpanded) {
        grid.classList.add(STATS_GRID_COLLAPSED_CLASS);
        toggle.setAttribute('aria-expanded', 'false');
      } else {
        grid.classList.remove(STATS_GRID_COLLAPSED_CLASS);
        toggle.setAttribute('aria-expanded', 'true');
      }
    });

    log('📊 Stats section collapse initialized (collapsed by default)');
  }

  let drawer = null;
  let drawerOverlay = null;
  let drawerClosing = false; // Prevent click-through when closing drawer

  // ---------------------------------------------------------------------------
  // Local event listener registry — mirrors core.js pattern for the drawer IIFE.
  // Tracks every persistent addEventListener so cleanup() can remove them all.
  // ---------------------------------------------------------------------------
  const _drawerListeners = []; // Array<{target, event, handler, options}>

  function registerDrawerListener(target, event, handler, options) {
    target.addEventListener(event, handler, options);
    _drawerListeners.push({ target, event, handler, options });
    return handler;
  }

  function removeDrawerListeners() {
    _drawerListeners.forEach(({ target, event, handler, options }) => {
      target.removeEventListener(event, handler, options);
    });
    _drawerListeners.length = 0;
  }

  /**
   * Create drawer HTML structure — builds overlay + drawer container, registers event handlers.
   * stopPropagation on drawer touchmove prevents body scroll when drawer is open (prevents scroll-while-reading).
   */
  function createDrawer() {
    // Create overlay
    drawerOverlay = document.createElement('div');
    drawerOverlay.className = 'mobile-tooltip-drawer-overlay';
    registerDrawerListener(drawerOverlay, 'click', closeDrawer, { passive: true });
    document.body.appendChild(drawerOverlay);

    // Create drawer
    drawer = document.createElement('div');
    drawer.className = 'mobile-tooltip-drawer';
    safeSetHTML(
      drawer,
      `
      <div class="mobile-drawer-handle"></div>
      <div class="mobile-drawer-header">
        <div class="mobile-drawer-title">Info</div>
        <button class="mobile-drawer-close">×</button>
      </div>
      <div class="mobile-drawer-content"></div>
    `
    );
    document.body.appendChild(drawer);

    // Close button handler (registered via registry so it is removed on teardown)
    registerDrawerListener(drawer.querySelector('.mobile-drawer-close'), 'click', closeDrawer, {
      passive: true,
    });

    // Prevent drawer scroll from propagating to body (belt-and-suspenders alongside CSS overscroll-behavior)
    const stopDrawerScroll = (e) => e.stopPropagation();
    registerDrawerListener(drawer, 'touchmove', stopDrawerScroll, { passive: true });
  }

  /**
   * Open drawer with tooltip content
   */
  function openDrawer(title, content, isHTML = false) {
    if (!drawer) createDrawer();

    drawer.querySelector('.mobile-drawer-title').textContent = title || 'Info';

    // Content may be a DOM node (preferred), an HTML string, or plain text.
    const contentEl = drawer.querySelector('.mobile-drawer-content');
    if (content instanceof Node) {
      while (contentEl.firstChild) contentEl.removeChild(contentEl.firstChild);
      contentEl.appendChild(content);
    } else if (isHTML) {
      safeSetHTML(contentEl, content);
    } else {
      contentEl.textContent = content;
    }

    // Show drawer
    requestAnimationFrame(() => {
      drawerOverlay.classList.add('active');
      drawer.classList.add('active');
    });

    // Prevent body scroll when drawer is open.
    // iOS Safari ignores overflow:hidden on body for touch events, so we also
    // use position:fixed (which works cross-browser) while preserving the
    // current scroll position so the page doesn't jump to the top.
    const scrollY = window.scrollY;
    document.body.style.overflow = 'hidden';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.width = '100%';
    document.body.dataset.scrollY = String(scrollY);
  }

  /**
   * Close drawer
   */
  function closeDrawer() {
    if (!drawer) return;

    // Set flag to prevent click-through to items underneath
    drawerClosing = true;

    drawerOverlay.classList.remove('active');
    drawer.classList.remove('active');

    // Restore scroll position before unlocking body.
    // On iOS Safari the position:fixed trick shifts layout — we must
    // scroll back to the stored position before removing position:fixed.
    const scrollY = parseInt(document.body.dataset.scrollY || '0', 10);
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.width = '';
    delete document.body.dataset.scrollY;
    window.scrollTo(0, scrollY);

    // Bug Fix 2: Use transitionend event and clear fallback timeout to prevent race condition
    const timeoutId = setTimeout(() => {
      drawerClosing = false;
    }, 500);

    drawer.addEventListener(
      'transitionend',
      () => {
        clearTimeout(timeoutId); // Clear fallback to prevent race condition
        drawerClosing = false;
      },
      { once: true }
    );
  }

  /**
   * Initialize drawer handlers for data-tooltip elements
   * Uses click event which doesn't interfere with scrolling
   */
  function initDrawerHandlers() {
    const tooltipElements = document.querySelectorAll('[data-tooltip]');

    tooltipElements.forEach((element) => {
      // Use click instead of touchstart to avoid blocking scroll
      element.addEventListener('click', function (e) {
        if (element.classList.contains('glass-card')) {
          const tooltipText = element.getAttribute('data-tooltip');
          if (tooltipText) {
            e.preventDefault();
            e.stopPropagation();
            const title = element.querySelector('h2')?.textContent || 'Information';
            openDrawer(title, tooltipText);
          }
        }
      });
    });
  }

  /**
   * Add drawer support for location items (visitor details) on mobile.
   * Order-row drawers are owned by admin-dashboard.js showOrderDrawer (single source).
   */
  function enhanceComplexTooltips() {
    // Location items - open drawer with user agent details
    const locationItems = document.querySelectorAll('.location-item:not([data-drawer-enhanced])');
    locationItems.forEach((item) => {
      item.dataset.drawerEnhanced = 'true';

      // Use click event for drawer
      item.addEventListener('click', function (e) {
        // Block clicks during drawer close animation to prevent click-through
        if (drawerClosing) return;

        // Payload is JSON, not markup: getAttribute decodes HTML entities, so
        // markup in an attribute would be re-parsed as live HTML on read.
        const payload = this.getAttribute('data-tooltip-data');
        if (payload) {
          e.preventDefault();
          e.stopPropagation();

          let tooltipData;
          try {
            tooltipData = JSON.parse(payload);
          } catch (_err) {
            return;
          }

          // Extract location name from the item
          const locationName =
            this.querySelector('.location-info strong')?.textContent || 'Location Details';

          openDrawer(locationName, window.AdminDashboardCore.buildLocationTooltip(tooltipData));
        }
      });

      // Keep visual feedback on touch
      item.addEventListener(
        'touchstart',
        function (_e) {
          this.classList.add('touch-active');
        },
        { passive: true }
      );

      item.addEventListener(
        'touchend',
        function () {
          const self = this;
          setTimeout(() => {
            self.classList.remove('touch-active');
          }, 300);
        },
        { passive: true }
      );
    });
  }

  /**
   * Initialize all mobile enhancements
   */
  function init() {
    // Touch-only module. Previously a top-level early return; moved here so the file can be
    // required (and therefore tested) without the IIFE exiting before it defines anything.
    if (!isTouchDevice) return;

    // Registered here rather than at IIFE top level so non-touch browsers still gain no listeners.
    registerTeardown();

    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', init);
      return;
    }

    log('📱 Initializing mobile drawer support for dashboard...');

    initStatsSectionCollapse();
    initDrawerHandlers();
    enhanceComplexTooltips();

    log('✅ Mobile drawer support enabled');
  }

  /**
   * Cleanup function to remove drawer listeners and prevent memory leaks.
   * Called on both beforeunload and visibilitychange(hidden) to ensure teardown in all
   * exit scenarios (tab close, navigation, and tab switch / screen lock).
   */
  function cleanup() {
    removeDrawerListeners();
  }

  /** Registers the unload/tab-hide teardown listeners exactly once. */
  let teardownRegistered = false;
  function registerTeardown() {
    if (teardownRegistered) return;
    teardownRegistered = true;
    window.addEventListener('beforeunload', cleanup);
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        cleanup();
      }
    });
  }

  /* eslint-disable no-undef -- `module` is absent on the storefront; the typeof guard is exactly what detects that. */
  if (typeof module !== 'undefined' && module.exports) {
    module.exports = {
      createDrawer: createDrawer,
      openDrawer: openDrawer,
      closeDrawer: closeDrawer,
      initDrawerHandlers: initDrawerHandlers,
      initStatsSectionCollapse: initStatsSectionCollapse,
      enhanceComplexTooltips: enhanceComplexTooltips,
      removeDrawerListeners: removeDrawerListeners,
      init: init,
      cleanup: cleanup,
    };
    return;
  }
  /* eslint-enable no-undef */

  // Initialize
  init();
})();
